import { useLayoutEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import type { TableCardFact } from "../cardInfo/tableCardFacts";
import {
  EMPTY_PLAN,
  defaultPlan,
  planCardFacts,
  type CardFactsPlan,
} from "../cardInfo/planCardFacts";
import {
  nameIsCut,
  nameLines,
  readCardFactsInput,
  type TableCardFitMode,
} from "../cardInfo/measureCardFacts";

/** Под тази ширина картата е „тясна": сервитьорът — само с инициали. */
const NARROW_CARD_WIDTH = 150;

// Колко реда от името на масата и от сервитьора се събират над долния ред
// (височината на картата е избраната, не по съдържанието): пише `--name-lines`
// и `--waiter-lines` направо в стила на горния ред — без прерисуване — и
// `data-clamp` на самия текст: при един ред той е на един ред с „…" (по букви,
// „Тераса ма…"), при повече — пренася се по думи и се реже на последния.
// Кегелът зависи от ширината на картата (`FIT`), затова редовата височина е
// от DOM.
const clampTopRow = (top: HTMLElement) => {
  const clamp = (selector: string, variable: string, offset: number) => {
    const element = top.querySelector<HTMLElement>(selector);
    if (!element) return;
    const lineHeight = parseFloat(getComputedStyle(element).lineHeight);
    const lines =
      lineHeight > 0 ? Math.max(1, Math.floor((top.clientHeight - offset + 0.5) / lineHeight)) : 1;
    top.style.setProperty(variable, String(lines));
    element.dataset.clamp = lines > 1 ? "lines" : "single";
  };
  clamp("[data-card-name]", "--name-lines", 0);
  clamp("[data-card-waiter]", "--waiter-lines", 3);
};

// Сервитьорът отстъпва на името на масата: тясна карта (под
// `NARROW_CARD_WIDTH`, където и кегелът на името намалява — `FIT.name`) или
// пълно име, което реже името на масата или го пренася на повече редове
// („59 / 8") — само инициалите („Т.П.", `data-waiter="initials"` на картата).
const fitWaiter = (card: HTMLElement, top: HTMLElement) => {
  const name = top.querySelector<HTMLElement>("[data-card-name]");
  if (!name || !top.querySelector("[data-card-waiter]")) {
    delete card.dataset.waiter;
    clampTopRow(top);
    return;
  }
  if (card.offsetWidth < NARROW_CARD_WIDTH) {
    card.dataset.waiter = "initials";
    clampTopRow(top);
    return;
  }
  card.dataset.waiter = "full";
  clampTopRow(top);
  const cutWithFullName = nameIsCut(name);
  const linesWithFullName = nameLines(name);
  card.dataset.waiter = "initials";
  clampTopRow(top);
  if (!cutWithFullName && nameLines(name) >= linesWithFullName) {
    card.dataset.waiter = "full";
    clampTopRow(top);
  }
};

// Ниската карта: сервитьорът е до сумата само ако има поне 60px за името си
// (`NameIfRoom` — иначе то отива на невидимия втори ред); не се ли събира —
// инициалите, по същия начин.
const fitOneLineWaiter = (card: HTMLElement) => {
  const room = card.querySelector<HTMLElement>("[data-waiter-room]");
  if (!room) {
    delete card.dataset.waiter;
    return;
  }
  card.dataset.waiter = "full";
  if (room.offsetTop > (room.parentElement?.offsetTop ?? 0) + 1) card.dataset.waiter = "initials";
};

// Мерките без информацията, както досега: ниската карта — сервитьорът до
// сумата; висока — сервитьорът и редовете на името. Телефонната — нищо.
const fitCard = (card: HTMLElement, mode: TableCardFitMode) => {
  if (mode === "oneLine") fitOneLineWaiter(card);
  if (mode !== "twoRow") return;
  const top = card.querySelector<HTMLElement>("[data-card-top]");
  if (top) fitWaiter(card, top);
};

// След като планът е в DOM: ниската карта — мястото на сервитьора е това,
// което информацията остави; високата — редовете долу смаляват горния ред.
const fitAfterPlan = (card: HTMLElement, mode: TableCardFitMode) => {
  if (mode === "oneLine") fitOneLineWaiter(card);
  if (mode !== "twoRow") return;
  const top = card.querySelector<HTMLElement>("[data-card-top]");
  if (top) clampTopRow(top);
};

// Плановете се сравняват по съдържание — `setPlan` само при разлика.
const samePlan = (left: CardFactsPlan, right: CardFactsPlan) =>
  JSON.stringify(left) === JSON.stringify(right);

// Мерената основа: местата на информацията се крият (`data-measuring` →
// `group-data-[measuring]/tablecard:hidden`), кутийката е точно като без нея;
// мерките на сервитьора и името — както досега, после планът.
const measurePlan = (
  card: HTMLElement,
  mode: TableCardFitMode,
  facts: readonly TableCardFact[],
): CardFactsPlan => {
  card.dataset.measuring = "";
  fitCard(card, mode);
  const input = readCardFactsInput(card, mode, facts);
  delete card.dataset.measuring;
  return planCardFacts(input);
};

/** Компактната кутийка расте по съдържанието — мери се наново само при нова
 *  ширина; десктопната е с размера от решетката. */
const sizeKeyOf = (card: HTMLElement, mode: TableCardFitMode) => {
  const { width, height } = card.getBoundingClientRect();
  return mode === "compact" ? String(width) : `${width}x${height}`;
};

interface MeasuredPlan {
  plan: CardFactsPlan;
  /** Видът и съдържанието, за които е мерен. */
  key: string;
}

/**
 * Съдържанието на картата на масата (`DesktopTableCard`, `TableCard`) по
 * размера ѝ — сервитьорът и редовете на името, както досега, и къде стоят
 * фактите за сметката (`planCardFacts`): наново при смяна на текста, който
 * влияе на мерките (`contentKey`: името, сервитьорът, сумата, фактите…), при
 * смяна на размера (дръжките, релсата) и веднъж след шрифтовете. Не при
 * всяко прерисуване: списъкът се опреснява често (WS, търсачката), а мерането
 * чете размерите от DOM. Кутийка без факти не мери нищо ново (`EMPTY_PLAN`).
 * Преди първото мерене и без размери (jsdom) — `defaultPlan`.
 */
export const useTableCardFit = (
  cardRef: RefObject<HTMLElement | null>,
  mode: TableCardFitMode,
  contentKey: string,
  facts: readonly TableCardFact[],
): CardFactsPlan => {
  const planKey = `${mode}|${contentKey}`;
  const [measured, setMeasured] = useState<MeasuredPlan | null>(null);
  // Последното мерене и фактите — за ResizeObserver и шрифтовете, които
  // мерят извън прерисуването.
  const measuredRef = useRef<MeasuredPlan | null>(null);
  const factsRef = useRef(facts);
  const fitRef = useRef<() => void>(() => {});

  useLayoutEffect(() => {
    factsRef.current = facts;
  });

  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const fit = () => {
      const currentFacts = factsRef.current;
      if (currentFacts.length === 0) {
        fitCard(card, mode);
        return;
      }
      const plan = measurePlan(card, mode, currentFacts);
      const previous = measuredRef.current;
      if (previous && previous.key === planKey && samePlan(previous.plan, plan)) {
        // Планът вече е в DOM — само горният ред/сервитьорът след него.
        fitAfterPlan(card, mode);
        return;
      }
      // До прерисуването с новия план (ResizeObserver, шрифтовете — извън
      // рендера) в DOM е старият: горният ред/сервитьорът — за него, не за
      // основата от мерането.
      fitAfterPlan(card, mode);
      measuredRef.current = { plan, key: planKey };
      setMeasured(measuredRef.current);
    };
    fitRef.current = fit;
    fit();
  }, [cardRef, mode, planKey]);

  // Новият план е нарисуван: редовете долу смаляват горния ред (`clampTopRow`).
  useLayoutEffect(() => {
    if (cardRef.current && measured) fitAfterPlan(cardRef.current, mode);
  }, [cardRef, mode, measured]);

  // Компактната кутийка без факти не мери нищо (както досега — без
  // наблюдател); десктопната — сервитьорът и името по размера, както досега.
  const hasFacts = facts.length > 0;
  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card || (mode === "compact" && !hasFacts)) return;
    let disposed = false;
    let lastSize = sizeKeyOf(card, mode);
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            const size = sizeKeyOf(card, mode);
            if (size === lastSize) return;
            lastSize = size;
            fitRef.current();
          });
    observer?.observe(card);
    // Шрифтовете (JetBrains Mono на сумата) идват след първото мерене — само
    // кутийките с факти мерят наново (другите — както досега) и само ако още
    // се зареждат (иначе мерането вече е с тях).
    if (document.fonts && document.fonts.status !== "loaded") {
      void document.fonts.ready.then(() => {
        if (!disposed && factsRef.current.length > 0) fitRef.current();
      });
    }
    return () => {
      disposed = true;
      observer?.disconnect();
    };
  }, [cardRef, mode, hasFacts]);

  if (facts.length === 0) return EMPTY_PLAN;
  return measured && measured.key === planKey ? measured.plan : defaultPlan(facts.length);
};
